import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import type { SigningResponse, SigningStatusResponse } from '@kin/property-tax-types';
import { County } from '../county/county-config';

export type { SigningResponse, SigningStatusResponse };
export type AuthorizationDuration = 'one-year' | 'ongoing';

const STORAGE_KEY = 'kin_signing_response';
const SIGNED_KEY = 'kin_signing_complete';

@Injectable({ providedIn: 'root' })
export class SigningService {
  private http = inject(HttpClient);

  private _signingResponse: SigningResponse | null = null;

  constructor() {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        this._signingResponse = JSON.parse(stored);
      } catch {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
  }

  get signingResponse(): SigningResponse | null {
    return this._signingResponse;
  }

  get isSigningComplete(): boolean {
    return sessionStorage.getItem(SIGNED_KEY) === 'true';
  }

  markSigningComplete(): void {
    sessionStorage.setItem(SIGNED_KEY, 'true');
  }

  /** Clears the cached signing response so the next request hits the API. */
  clear(): void {
    this._signingResponse = null;
    sessionStorage.removeItem(STORAGE_KEY);
  }

  /** Fully resets all signing state including the signing-complete flag. */
  fullReset(): void {
    this.clear();
    sessionStorage.removeItem(SIGNED_KEY);
  }

  createSignatureRequest(
    authorizationDuration: AuthorizationDuration = 'one-year',
    county: County,
  ): Observable<SigningResponse> {
    const body = { authorizationDuration, county };

    if (this._signingResponse) {
      // Return cache immediately, but refresh in the background
      const cached = this._signingResponse;
      this.http.post<SigningResponse>(`${environment.apiUrl}/signing/request`, body).pipe(
        tap(e => this.save(e)),
      ).subscribe();
      return of(cached);
    }
    return this.http.post<SigningResponse>(`${environment.apiUrl}/signing/request`, body).pipe(
      tap(e => this.save(e)),
    );
  }

  getSignatureRequestStatus(signatureRequestId: string): Observable<SigningStatusResponse> {
    return this.http.get<SigningStatusResponse>(`${environment.apiUrl}/signing/request/${signatureRequestId}/status`);
  }

  private save(response: SigningResponse): void {
    this._signingResponse = response;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(response));
  }
}
